import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { forkJoin, of, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule, ProgressBar } from 'primeng/progressbar';
import { CommonModule } from '@angular/common';
import { EntityDropdownPickerComponent } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { ActivityType } from '../../master-data/activity-type/activity-type.model';
import { ActivityTypeService } from '../../master-data/activity-type/activity-type.service';
import { PromoPlanHeader } from '../../promo-plan/promo-plan.model';
import { HttpParams } from '@angular/common/http';
import { PromoPlanSearchParams, PromoPlanService } from '../../promo-plan/promo-plan.service';
import { Settlement } from '../settlement.model';
import { SettlementService } from '../settlement.service';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';
import { BusinessUnit } from '../../master-data/business-units/business-unit.model';
import { BusinessUnitsService } from '../../master-data/business-units/business-units.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { CustomerPickerSummary } from '../../master-data/customers/customers.model';
import { Customer } from '../../master-data/customers/customers.model';
import { Page } from '../../models/page-wrapper.model';
import { Location } from "../../master-data/customers/locations/locations.model";
import { AppUser } from '../../users/user.model';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { filter, map, take, switchMap } from 'rxjs/operators';
import { toNormalizedDate } from '../../shared/utils/date-normalization.util';


@Component({
  selector: 'app-add-edit-settlement',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    FieldsetModule,
    ButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    FileUploadModule,
    EntityDropdownPickerComponent,
    ProgressBar
],
  providers: [MessageService],
  templateUrl: './add-edit-settlement.component.html',
  styleUrls: ['./add-edit-settlement.component.css']
})
export class AddEditSettlementComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;

  settlementForm!: FormGroup;
  isSaving = false;
  submitted = false;
  isEditMode = false;
  settlementId: string | null = null;
  currentUser: AppUser;

  // Breadcrumb items
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;

  locations: Location[] = [];
  filteredLocations: Location[] = [];

  businessUnits: BusinessUnit[] = [];
  businessUnitPickerAdapter!: EntityDropdownPickerAdapter<BusinessUnit>;
  activityTypes: ActivityType[] = [];
  activityTypePickerAdapter!: EntityDropdownPickerAdapter<ActivityType>;

  isPromoPlansLoading = false;
  notSettledPromoPlans: PromoPlanHeader[] = [];
  filteredPromoPlans: PromoPlanHeader[] = [];
  promoPlanPickerAdapter!: EntityDropdownPickerAdapter<PromoPlanHeader>;
  isUploadingAttachments = false;
  attachmentUploadProgress = 0;

  private valueChangesSub!: Subscription;
  private attachmentProgressByIndex = new Map<number, number>();

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private businessService: BusinessService,
    private promoPlanService: PromoPlanService,
    private customerService: CustomersService,
    private businessUnitService: BusinessUnitsService,
    private settlementService: SettlementService,
    private activityTypeService: ActivityTypeService,
    private route: ActivatedRoute,
    private storageService: FirebaseStorageService
  ) {
    this.currentUser = JSON.parse(sessionStorage.getItem('appUser') ?? '') as AppUser;
  }

  ngOnInit(): void {
    this.customerPickerAdapter = this.customerService.createCustomerPickerAdapter();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();
    this.businessUnitPickerAdapter = this.businessUnitService.createBusinessUnitPickerAdapter();
    this.promoPlanPickerAdapter = this.createPromoPlanPickerAdapter();
    this.activityTypePickerAdapter = this.createActivityTypePickerAdapter();

    this.settlementId = this.route.snapshot.paramMap.get('settlementId');
    this.isEditMode = !!this.settlementId;

    this.initForm();
    this.subscribeToValueChanges();

    // Load all necessary dropdown data and then proceed with form population
    this.loadDropdownData().subscribe({
      next: (data) => {
        // Assign data to component properties
        this.notSettledPromoPlans = data.promoPlans.content;
        this.filteredPromoPlans = data.promoPlans.content;
        this.isPromoPlansLoading = false;

        this.customers = data.customers.content;
        this.filteredCustomers = data.customers.content;
        this.settlementForm.get('location')?.disable();

        this.businesses = data.businesses.content;
        this.filteredBusinesses = data.businesses.content;
        this.activityTypes = data.activityTypes.content;

        this.businessUnits = data.businessUnits.content;
        this.settlementForm.get('businessUnit')?.disable();

        // Now that dropdowns are populated, we can safely check for pre-fill or edit mode.
        if (this.isEditMode) {
          this.settlementService.getSettlementById(+this.settlementId!).subscribe(settlement => {

            // Find the correct object references from the dropdown lists to ensure proper binding.
            const customerToSet = this.customers.find(c => c.id === settlement.customer?.id);
            const businessToSet = this.businesses.find(b => b.id === settlement.business?.id);
            const locationToSet = customerToSet?.locations.find(l => l.id === settlement.location?.id);
            const businessUnitToSet = this.businessUnits.find(bu => bu.id === settlement.businessUnit?.id);
            const activityTypeToSet = this.activityTypes.find(at => at.id === settlement.activityType?.id);

            // Pre-fill the form with existing settlement data
            this.settlementForm.patchValue({
              promoPlan: settlement?.promoPlanId ? this.notSettledPromoPlans.find(p => p.promoPlanId === settlement.promoPlanId) : null,
              startDate: toNormalizedDate(settlement.startDate),
              endDate: toNormalizedDate(settlement.endDate),
              actualStartDate: settlement.promoPlanId ? toNormalizedDate(settlement.actualStartDate) : null,
              actualEndDate: settlement.promoPlanId ? toNormalizedDate(settlement.actualEndDate) : null,
              customer: customerToSet,
              location: locationToSet,
              business: businessToSet,
              businessUnit: businessUnitToSet,
              activityType: activityTypeToSet,
              note: settlement.note,
              value: settlement.value,
              vat: settlement.vat,
              totalAmount: settlement.totalAmount,
              serviceInvoice: settlement.serviceInvoice,
              salesRep: settlement.salesRep,
              customerInvoiceNumber: settlement.customerInvoiceNumber,
            });

            // Re-enable fields that might have been disabled by prefillFormFromPromoPlan if no promo plan is linked
            if (!settlement.promoPlanId) {
              this.enablePromoFields();
            } else {
              // If there is a promo plan, ensure the fields remain disabled as per prefillFormFromPromoPlan logic
              this.settlementForm.get('startDate')?.disable();
              this.settlementForm.get('endDate')?.disable();
              this.settlementForm.get('customer')?.disable();
              this.settlementForm.get('activityType')?.disable();
              this.settlementForm.get('business')?.disable();
              this.settlementForm.get('businessUnit')?.disable();
              this.settlementForm.get('actualStartDate')?.enable();
              this.settlementForm.get('actualEndDate')?.enable();
            }

            // Load attachments into the form array
            settlement.attachments?.forEach(attachment => this.attachments.push(this.fb.group(attachment)));
          });
        } else {
          // Check for a promoPlanId from the route's query parameters to pre-fill the form.
          const promoPlanIdParam = this.route.snapshot.queryParamMap.get('promoPlanId');
          if (promoPlanIdParam) {
            const promoPlanId = +promoPlanIdParam; // Safer conversion
            this.promoPlanService.getPromoPlanHeaderById(promoPlanId).subscribe({
              next: (promoPlan) => {
                // Set the value of the promoPlan dropdown. This will trigger the valueChanges
                // subscription which calls prefillFormFromPromoPlan.
                this.settlementForm.get('promoPlan')?.setValue(promoPlan);
              },
              error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data from Promo Plan.' });
                console.error(`Error pre-filling form from promo plan ${promoPlanId}:`, err);
              }
            });
          }
        }
      },
      error: (err) => {
        this.isPromoPlansLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load required form data.' });
        console.error('Error loading dropdown data:', err);
      }
    });
  }

  private createPromoPlanPickerAdapter(): EntityDropdownPickerAdapter<PromoPlanHeader> {
    return {
      entityKey: 'promo-plan',
      loadRecent: (limit?: number) => this.promoPlanService.loadRecentPromoPlans(limit),
      search: (query: string, page: number, size: number) => this.searchPromoPlans(query, page, size),
      getFreshById: (id: number | string) => this.promoPlanService.getFreshPromoPlanById(id),
      rememberSelection: (promoPlan: PromoPlanHeader) => this.promoPlanService.rememberPromoPlan(promoPlan),
      getId: (promoPlan: PromoPlanHeader) => promoPlan.promoPlanId ?? 0,
      getLabel: (promoPlan: PromoPlanHeader) => `Promo Plan# ${promoPlan.promoPlanId} - ${promoPlan.promoDescription}`,
      getSecondaryLabel: (promoPlan: PromoPlanHeader) => `${promoPlan.customer?.customerNumber ?? ''} - ${promoPlan.customer?.customerName ?? ''}`,
      filterBy: 'promoPlanId',
    };
  }

  private createActivityTypePickerAdapter(): EntityDropdownPickerAdapter<ActivityType> {
    return {
      entityKey: 'activity-type',
      loadRecent: () => of([] as ActivityType[]),
      search: (query: string, page: number, size: number) => this.searchActivityTypes(query, page, size),
      getFreshById: (id: number | string) => this.activityTypeService.getActivityTypeById(Number(id)),
      rememberSelection: () => undefined,
      getId: (activityType: ActivityType) => activityType.id,
      getLabel: (activityType: ActivityType) => activityType.name,
      getSecondaryLabel: (activityType: ActivityType) => activityType.icon ?? '',
      filterBy: 'name',
    };
  }

  private searchPromoPlans(query: string, page: number, size: number) {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      const content = this.notSettledPromoPlans.slice(page * size, page * size + size);
      return of({
        content,
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: { sorted: false, unsorted: true, empty: true },
          offset: page * size,
          paged: true,
          unpaged: false,
        },
        last: page * size + content.length >= this.notSettledPromoPlans.length,
        totalPages: Math.ceil(this.notSettledPromoPlans.length / size),
        totalElements: this.notSettledPromoPlans.length,
        size,
        number: page,
        sort: { sorted: false, unsorted: true, empty: true },
        first: page === 0,
        numberOfElements: content.length,
        empty: this.notSettledPromoPlans.length === 0,
      } as Page<PromoPlanHeader>);
    }

    const promoPlanId = Number(normalized);
    if (Number.isNaN(promoPlanId)) {
      return of({
        content: [],
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: { sorted: false, unsorted: true, empty: true },
          offset: page * size,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 0,
        totalElements: 0,
        size,
        number: page,
        sort: { sorted: false, unsorted: true, empty: true },
        first: true,
        numberOfElements: 0,
        empty: true,
      } as Page<PromoPlanHeader>);
    }

    return this.promoPlanService.searchPromoPlanHeaders({
      promoPlanIds: [promoPlanId],
      statuses: ['Not Settled'],
      page,
      size,
      sort: 'promoPlanId,desc',
    });
  }

  private searchActivityTypes(query: string, page: number, size: number) {
    const normalized = query.trim().toLowerCase();
    const filtered = !normalized
      ? this.activityTypes
      : this.activityTypes.filter((activityType) => {
          const label = `${activityType.name ?? ''} ${activityType.icon ?? ''}`.toLowerCase();
          return label.includes(normalized);
        });

    const content = filtered.slice(page * size, page * size + size);
    return of({
      content,
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: page * size + content.length >= filtered.length,
      totalPages: Math.ceil(filtered.length / size),
      totalElements: filtered.length,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: content.length,
      empty: filtered.length === 0,
    } as Page<ActivityType>);
  }

  private initForm(): void {
    this.settlementForm = this.fb.group({
      // Settlement Request
      promoPlan: [null],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      actualStartDate: [null],
      actualEndDate: [null],
      customer: [null, Validators.required],
      location: [null, Validators.required],
      business: [null, Validators.required],
      businessUnit: [null, Validators.required],
      activityType: [null, Validators.required],
      year: [new Date().getFullYear(), [Validators.required, Validators.min(2000)]],
      period: [new Date().getMonth() + 1, [Validators.required, Validators.min(1), Validators.max(12)]],
      note: [''],
      value: [0, [Validators.required, Validators.min(-999999999999)]],
      vat: [0, [Validators.required, Validators.min(-999999999999)]],
      serviceInvoice: [false],
      totalAmount: [{ value: 0, disabled: true }],
      salesRep: [{ value: null, disabled: true }],
      customerInvoiceNumber: [null],

      // Attachments
      attachments: this.fb.array([])
    });

    this.settlementForm.get('actualStartDate')?.disable();
    this.settlementForm.get('actualEndDate')?.disable();
  }

  private subscribeToValueChanges(): void {
    this.valueChangesSub = new Subscription();

    this.valueChangesSub.add(
      this.settlementForm.get('activityType')?.valueChanges.subscribe((activityType: ActivityType) => {
        if (activityType) {
          this.isPromoPlansLoading = true;
          const searchParams: PromoPlanSearchParams = {
            statuses: ['Not Settled'],
            promoTypes: [activityType.name],
            page: 0,
            size: 100,
            sort: 'promoPlanId,desc'
          };
          this.promoPlanService.searchPromoPlanHeaders(searchParams).subscribe({
            next: (data) => {
              // Assign data to component properties
              this.notSettledPromoPlans = data.content;
              this.filteredPromoPlans = data.content;
              this.isPromoPlansLoading = false;
            },
            error: (err) => {
              this.isPromoPlansLoading = false;
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load required form data.' });
              console.error('Error loading dropdown data:', err);
            }
          });
        }
      })
    );

    // Update dependent fields when customer changes
    this.valueChangesSub.add(
      this.settlementForm.get('customer')?.valueChanges.subscribe((customer: Customer) => {
        this.updateLocationOptions(customer ?? null);
      })
    );

    // Update dependent fields when customer changes
    this.valueChangesSub.add(
      this.settlementForm.get('business')?.valueChanges.subscribe((business: Business) => {
        if (business) {
          this.settlementForm.get('businessUnit')?.setValue(this.businessUnits.filter(bu => bu.id === business.businessUnitId)?.at(0));
        }
      })
    );

    this.valueChangesSub.add(
      this.settlementForm.get('promoPlan')?.valueChanges.subscribe((promoPlan: PromoPlanHeader) => {
        if (promoPlan) {
          // The promoPlan object is available, so we can pre-fill the dependent fields directly.
          this.prefillFormFromPromoPlan(promoPlan);
        }
      })
    );

    // Calculate Total Amount
    this.valueChangesSub.add(
      this.settlementForm.get('value')?.valueChanges.subscribe(() => this.calculateTotal())
    );
    this.valueChangesSub.add(
      this.settlementForm.get('vat')?.valueChanges.subscribe(() => this.calculateTotal())
    );

    this.valueChangesSub.add(
      this.settlementForm.get('startDate')?.valueChanges.subscribe((startDate: Date) => {
        if (startDate && !this.settlementForm.get('promoPlan')?.value) {
          this.settlementForm.get('actualStartDate')?.setValue(startDate);
        }
      })
    );

    this.valueChangesSub.add(
      this.settlementForm.get('endDate')?.valueChanges.subscribe((endDate: Date) => {
        if (endDate && !this.settlementForm.get('promoPlan')?.value) {
          this.settlementForm.get('actualEndDate')?.setValue(endDate);
        }
      })
    );
  }

  private calculateTotal(): void {
    const value = this.settlementForm.get('value')?.value || 0;
    const vat = this.settlementForm.get('vat')?.value || 0;
    this.settlementForm.get('totalAmount')?.setValue(value + vat, { emitEvent: false });
  }

  private updateLocationOptions(customer: Customer | null): void {
    const locationControl = this.settlementForm.get('location');

    if (!customer || !Array.isArray(customer.locations) || customer.locations.length === 0) {
      this.locations = [];
      this.filteredLocations = [];
      locationControl?.reset(null, { emitEvent: false });
      locationControl?.disable({ emitEvent: false });
      return;
    }

    this.locations = customer.locations;
    this.filteredLocations = [...customer.locations];

    if (locationControl?.disabled) {
      locationControl.enable({ emitEvent: false });
    }
  }

  onCustomerSelectionLoaded(customer: Customer | null): void {
    this.updateLocationOptions(customer);
  }

  private prefillFormFromPromoPlan(promoPlan: PromoPlanHeader): void {
    // Ensure the fetched promo plan is available for selection in the dropdown.
    if (!this.notSettledPromoPlans.some(p => p.promoPlanId === promoPlan.promoPlanId)) {
      this.notSettledPromoPlans.unshift(promoPlan);
      this.filteredPromoPlans.unshift(promoPlan);
    }

    // Find the correct object references from the dropdown lists to ensure proper binding.
    const customerToSet = this.customers.find(c => c.id === promoPlan.customer?.id);
    const businessToSet = this.businesses.find(b => b.id === promoPlan.business?.id);
    const businessUnitToSet = this.businessUnits.find(bu => bu.id === promoPlan.businessUnit?.id);
    const activityTypeToSet = this.activityTypes.find(a => a.id === promoPlan.activityType?.id);

    // Using patchValue and allowing events to emit (the default behavior) will
    // trigger the valueChanges subscriptions to update dependent fields like 'location' and 'businessUnit'.
    this.settlementForm.patchValue({
      startDate: new Date(promoPlan.promoStartDate),
      endDate: new Date(promoPlan.promoEndDate),
      customer: customerToSet,
      business: businessToSet,
      businessUnit: businessUnitToSet,
      activityType: activityTypeToSet,
      actualStartDate: new Date(promoPlan.promoStartDate),
      actualEndDate: new Date(promoPlan.promoEndDate),
    });

    // Disable and make readonly the specified fields
    this.settlementForm.get('startDate')?.disable();
    this.settlementForm.get('endDate')?.disable();
    this.settlementForm.get('customer')?.disable();
    this.settlementForm.get('activityType')?.disable();
    this.settlementForm.get('business')?.disable();
    this.settlementForm.get('businessUnit')?.disable();
    this.settlementForm.get('actualStartDate')?.enable();
    this.settlementForm.get('actualEndDate')?.enable();

    this.messageService.add({ severity: 'info', summary: 'Pre-filled', detail: `Form pre-filled from Promo Plan #${promoPlan.promoPlanId}` });
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.settlementForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      return;
    }

    const settlementData: Settlement = {
      promoPlanId: this.settlementForm.get('promoPlan')?.value?.promoPlanId,
      startDate: this.settlementForm.get('startDate')?.value,
      endDate: this.settlementForm.get('endDate')?.value,
      actualStartDate: this.settlementForm.get('actualStartDate')?.value,
      actualEndDate: this.settlementForm.get('actualEndDate')?.value,
      customer: this.settlementForm.get('customer')?.value,
      location: this.settlementForm.get('location')?.value,
      channel: this.settlementForm.get('customer')?.value?.channel,
      business: this.settlementForm.get('business')?.value,
      businessUnit: this.settlementForm.get('businessUnit')?.value,
      activityType: this.settlementForm.get('activityType')?.value,
      note: this.settlementForm.get('note')?.value,
      value: this.settlementForm.get('value')?.value,
      vat: this.settlementForm.get('vat')?.value,
      totalAmount: this.settlementForm.get('totalAmount')?.getRawValue(),
      serviceInvoice: this.settlementForm.get('serviceInvoice')?.value ?? false,
      customerInvoiceNumber: this.settlementForm.get('customerInvoiceNumber')?.value,
      salesRep: this.currentUser
      // Manually extract values from form controls
      // status: this.settlementForm.getRawValue().status, 
      // textReference: this.settlementForm.getRawValue().textReference,
    };
    settlementData.branch = settlementData.location?.branch;

    this.isSaving = true;
    const saveObservable = this.isEditMode
      ? this.settlementService.updateSettlement(+this.settlementId!, settlementData)
      : this.settlementService.createSettlement(settlementData);

    saveObservable.subscribe({
      next: (savedSettlement) => {
        if (!savedSettlement.id) {
          this.isSaving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Settlement saved, but the backend did not return an id for attachment upload.',
          });
          return;
        }

        this.uploadSettlementAttachments(savedSettlement.id).subscribe({
          next: () => {
            this.isSaving = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Settlement saved successfully!' });
            this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['settlement', 'view', savedSettlement.id] } }]);
          },
          error: (err) => {
            this.isSaving = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Settlement saved, but attachment upload failed.' });
            console.error('Attachment upload failed after settlement save:', err);
          }
        });
      },
      error: (err) => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save settlement:' + err.error.message });
        console.error('Save settlement error:', err);
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/settlements']); // Or use Location.back()
  }

  private loadDropdownData() {
    // Load Promo Plans
    this.isPromoPlansLoading = true;
    const searchParams: PromoPlanSearchParams = {
      statuses: ['Not Settled'],
      page: 0,
      size: 100,
      sort: 'promoPlanId,desc'
    };
    return forkJoin({
      promoPlans: this.promoPlanService.searchPromoPlanHeaders(searchParams),
      customers: this.customerService.getAllCustomers({ page: 0, size: 10000, loadLocations: true }),
      businesses: this.businessService.getAllBusinesss({ page: 0, size: 10000 }),
      activityTypes: this.activityTypeService.getAllActivityTypes({ page: 0, size: 100 }),
      businessUnits: this.businessUnitService.getAllBusinessUnits({ page: 0, size: 10000 })
    });
  }

  getDropdownLabel(item: any): string {
    return item?.name || item?.customerName || 'N/A';
  }

  filterPromoPlans(query: string) {
    if (!query) {
      this.filteredPromoPlans = this.notSettledPromoPlans;
      return;
    }

    const localFiltered = this.notSettledPromoPlans.filter(promoPlan =>
      promoPlan.promoDescription.toLowerCase().includes(query.toLowerCase())
      || promoPlan.promoPlanId?.toString().includes(query)
    );

    if (localFiltered.length > 0) {
      this.filteredPromoPlans = localFiltered;
    } else {
      if (!isNaN(Number(query))) {
        this.promoPlanService.getPromoPlanHeaderById(Number(query)).subscribe(promoPlan => {
          // Combine with existing selections to not lose them
          if (promoPlan.workflowStatus?.statusName === 'Not Settled') {
            const selectedPromoPlans = this.settlementForm.get('promoPlan')?.value || [];
            this.filteredPromoPlans = [...new Set([...selectedPromoPlans, promoPlan])];
          } else this.messageService.add({ severity: 'error', summary: 'Error', detail: 'This promo plan is not in Not Settled status'});
        });
      }
    }
  }

  filterCustomers(event: any) {
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(customer =>
        customer.customerName.toLowerCase().includes(event.target.value.toLowerCase())
        || customer.customerNumber.toLowerCase().includes(event.target.value.toLowerCase())
      );
    }
  }

  filterBusinesses(event: any) {
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(business =>
        business.businessName.toLowerCase().includes(event.target.value.toLowerCase())
      );
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
    }
  }

  // Getter for easy access to the attachments FormArray in the template
  get attachments(): FormArray {
    return this.settlementForm.get('attachments') as FormArray;
  }

  private uploadSettlementAttachments(settlementId: number) {
    const pendingAttachments = this.attachments.controls
      .filter(control => !control.get('attachmentId')?.value && control.get('file')?.value)
      .map(control => ({
        file: control.get('file')?.value as File,
        fileName: control.get('fileName')?.value as string,
        fileType: control.get('fileType')?.value as string,
        fileSize: control.get('fileSize')?.value as number,
      }));

    if (pendingAttachments.length === 0) {
      return of([]);
    }

    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();
    const settlementFolder = `settlements/${settlementId}`;

    return forkJoin(
      pendingAttachments.map((item, index) => {
        const safeFileName = item.fileName.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${settlementFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(item.file, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, pendingAttachments.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: item.fileName,
            fileType: item.fileType,
            fileSize: item.fileSize,
            fileUrl: event.downloadURL!,
            uploadedBy: this.currentUser?.fullName || this.currentUser?.email || 'N/A',
            uploadedDate: new Date().toISOString()
          }))
        );
      })
    ).pipe(
      switchMap(uploadedAttachments => this.settlementService.uploadAttachments(settlementId, uploadedAttachments)),
      map(savedAttachments => {
        this.isUploadingAttachments = false;
        return savedAttachments;
      })
    );
  }

  onFileSelect(event: any): void {
    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    files.forEach(file => {
      this.attachments.push(this.fb.group({
        fileName: [file.name],
        fileType: [file.type],
        fileSize: [file.size],
        file: [file],
      }));
    });
  }

  removeAttachment(index: number): void {
    this.attachments.removeAt(index);
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  enablePromoFields() {
    this.settlementForm.get('startDate')?.enable();
    this.settlementForm.get('endDate')?.enable();
    this.settlementForm.get('customer')?.enable();
    this.settlementForm.get('activityType')?.enable();
    this.settlementForm.get('business')?.enable();
    this.settlementForm.get('businessUnit')?.enable();
    this.settlementForm.get('actualStartDate')?.disable();
    this.settlementForm.get('actualEndDate')?.disable();
  }
}
